<!-- Home "Tasks" view: one row per inline task. Clicking a task opens its note in Edit (cursor at the end
     of the task); the Completed cell toggles the task (click once to pick the cell, again to toggle). -->
<ng-template #completedMark let-row>
  <span
    class="inline-flex items-center gap-1 whitespace-nowrap"
    [ngClass]="row.task.completed ? 'text-emerald-700 dark:text-emerald-400' : row.overdue ? '' : 'text-[var(--textColorFaded)]'"
    data-testid="task-completed">
    <i class="pi" [ngClass]="row.task.completed ? 'pi-check-square' : 'pi-stop'" aria-hidden="true"></i>{{ row.task.completed ? 'Done' : 'Open' }}
  </span>
</ng-template>

<ng-template #dueMark let-row>
  @if (row.task.dueDate) {
    <span
      class="inline-flex items-center gap-1 whitespace-nowrap"
      [ngClass]="{ 'font-semibold': row.overdue }"
      [title]="row.overdue ? 'Overdue — was due ' + row.dueLong : 'Due ' + row.dueLong"
      data-testid="task-due">
      <i class="pi text-xs" [ngClass]="row.overdue ? 'pi-exclamation-circle' : 'pi-calendar'" aria-hidden="true"></i>
      @if (row.overdue) {<span class="sr-only">Overdue:</span>}{{ row.dueLabel }}
    </span>
  }
</ng-template>

<div class="p-1 print:hidden">
  @if (isSelectMode) {
    <!-- Select mode (tasks): the same bar as the notes' Select mode, with task actions. -->
    <div
      class="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-primary/60 bg-primary/15 px-3 py-2 text-sm text-primary-900 shadow-inner dark:text-primary-100"
      data-testid="task-select-bar">
      <div class="flex flex-wrap items-center gap-2">
        <p-menu #bulkMenu [model]="bulkMenuItems" [popup]="true" appendTo="body"></p-menu>
        <button
          type="button"
          class="min-h-11 rounded-full border border-primary/50 px-4 py-1 text-xs font-semibold tracking-wide text-primary-700 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent sm:min-h-0 dark:text-primary-200 dark:hover:bg-slate-800"
          [disabled]="selectedCount === 0 || busy"
          [title]="selectedCount === 0 ? 'Select one or more tasks first' : ''"
          (click)="bulkMenu.toggle($event)"
          data-testid="task-bulk-actions">
          <span class="flex items-center gap-1">Actions <i class="pi pi-chevron-down text-[10px]" aria-hidden="true"></i></span>
        </button>
        <button
          type="button"
          class="min-h-11 rounded-full border border-primary/50 px-4 py-1 text-xs font-semibold tracking-wide text-primary-700 transition hover:bg-white disabled:opacity-40 sm:min-h-0 dark:text-primary-200 dark:hover:bg-slate-800"
          [disabled]="rows.length === 0 || allShownSelected"
          (click)="selectAllShown()"
          data-testid="task-select-all">
          Select all {{ rows.length | number }} {{ rows.length === 1 ? 'task' : 'tasks' }}
        </button>
        <button
          type="button"
          class="min-h-11 rounded-full border border-primary/50 px-4 py-1 text-xs font-semibold tracking-wide text-primary-700 transition hover:bg-white disabled:opacity-40 sm:min-h-0 dark:text-primary-200 dark:hover:bg-slate-800"
          [disabled]="selectedCount === 0"
          (click)="clearSelection()">
          Clear Selection
        </button>
        <div class="ml-1">
          <div class="font-semibold tracking-wide">Select mode active</div>
          <div class="text-xs text-primary-700 dark:text-primary-200" aria-live="polite" data-testid="task-selected-count">
            {{ selectedCount }} task(s) selected
          </div>
        </div>
      </div>
      <button
        type="button"
        class="min-h-11 rounded-full bg-primary px-4 py-1 text-xs font-semibold tracking-wide text-white shadow hover:bg-primary-600 sm:min-h-0"
        (click)="exitSelectMode.emit()"
        data-testid="task-exit-select">
        Exit Select Mode
      </button>
    </div>
  }

  <!-- Count, the view's filters (Open/Closed, due date) and, on phones, the sort control. -->
  <div class="flex flex-wrap items-center gap-x-3 gap-y-2 px-1 py-1 text-sm">
    <span class="font-medium text-[var(--textColor)]" aria-live="polite" data-testid="task-count">
      {{ loading ? 'Loading tasks…' : countLabel }}
    </span>
    <p-selectbutton
      [options]="statusFilterOptions"
      optionLabel="label"
      optionValue="value"
      [ngModel]="statusFilter"
      (ngModelChange)="setStatusFilter($event)"
      [allowEmpty]="false"
      size="small"
      ariaLabelledBy="taskStatusFilterLabel"
      styleClass="task-status-filter"
      data-testid="task-status-filter"></p-selectbutton>
    <span id="taskStatusFilterLabel" class="sr-only">Show tasks</span>
    <p-select
      inputId="taskDueFilter"
      [options]="dueFilterOptions"
      optionLabel="label"
      optionValue="value"
      [ngModel]="dueFilter"
      (ngModelChange)="setDueFilter($event)"
      appendTo="body"
      size="small"
      ariaLabel="Filter by due date"
      [styleClass]="dueFilter !== 'any' ? 'task-due-filter task-due-filter--active' : 'task-due-filter'"
      data-testid="task-due-filter"></p-select>
    @if (!isLargeScreen) {
      <div class="ml-auto flex items-center gap-1">
        <label class="text-xs text-[var(--textColorFaded)]" for="taskSortField">Sort by</label>
        <p-select
          inputId="taskSortField"
          [options]="sortOptions"
          optionLabel="label"
          optionValue="value"
          [ngModel]="sortBy"
          (ngModelChange)="setSortField($event)"
          appendTo="body"
          styleClass="!min-h-11"></p-select>
        <button
          type="button"
          class="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md border border-gray-300 bg-transparent text-[var(--textColor)] dark:border-slate-600"
          (click)="toggleSortOrder()"
          [attr.aria-label]="sortOrder === 'asc' ? 'Sorted ascending — sort descending' : 'Sorted descending — sort ascending'"
          data-testid="task-sort-order">
          <span class="material-icons text-base" aria-hidden="true">{{ sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
        </button>
      </div>
    }
  </div>

  @if (!isLargeScreen) {
    <!-- Phones/tablets: 2-line card rows (task, then note · owner · due), like the List view's cards.
         The status icon on the left is the Completed control (tap once to pick, again to toggle). -->
    <div role="list" class="divide-y divide-gray-300 overflow-hidden rounded-md border-b border-t border-gray-300 dark:divide-slate-600 dark:border-slate-600">
      @if (loading) {
        <div class="px-3 py-6 text-center text-sm text-[var(--textColorFaded)]" data-testid="task-loading">Loading tasks…</div>
      } @else {
        @for (row of rows; track row.key) {
          <div
            role="listitem"
            tabindex="0"
            class="flex min-h-11 cursor-pointer items-start gap-1 bg-surface-0 py-1 pr-3 text-black hover:bg-gray-100 dark:bg-surface-900 dark:text-slate-100 dark:hover:!bg-slate-800"
            [ngClass]="{ 'task-overdue !text-red-700 dark:!text-red-400': row.overdue, 'ring-2 ring-inset ring-primary-600': isSelectMode && isSelected(row) }"
            [attr.aria-label]="(row.overdue ? 'Overdue: ' : row.task.completed ? 'Done: ' : 'Open: ') + row.task.description + ', in ' + row.noteTitle + (isSelectMode ? (isSelected(row) ? '. Selected.' : '.') : '. Opens the note in Edit.')"
            (click)="onRowClick(row)"
            (keydown.enter)="onRowClick(row)"
            data-testid="task-row">
            @if (isSelectMode) {
              <span class="flex min-h-11 min-w-9 flex-shrink-0 items-center justify-center" (click)="$event.stopPropagation()">
                <p-checkbox
                  [binary]="true"
                  [ngModel]="isSelected(row)"
                  (ngModelChange)="toggleSelected(row, $event)"
                  [ariaLabel]="'Select task ' + row.task.description"
                  data-testid="task-select"></p-checkbox>
              </span>
            }
            <button
              type="button"
              class="inline-flex min-h-11 min-w-11 flex-shrink-0 items-center justify-center rounded-md border-0 bg-transparent"
              [ngClass]="[
                row.task.completed ? 'text-emerald-700 dark:text-emerald-400' : row.overdue ? 'text-inherit' : 'text-[var(--textColorFaded)]',
                activeCompletedKey === row.key ? 'task-completed-cell--active' : ''
              ]"
              [title]="completedCellTitle(row)"
              [attr.aria-pressed]="row.task.completed"
              [attr.aria-label]="(row.task.completed ? 'Done' : 'Open') + (activeCompletedKey === row.key ? ' — tap again to toggle' : ' — tap to select, again to toggle')"
              (click)="onCompletedCellClick(row, $event)"
              (keydown.enter)="$event.stopPropagation()"
              data-testid="task-completed-cell">
              <i class="pi" [ngClass]="row.task.completed ? 'pi-check-square' : 'pi-stop'" aria-hidden="true"></i>
            </button>
            <div class="min-w-0 flex-1 py-1">
              <div class="truncate" [ngClass]="{ 'text-gray-500 line-through dark:text-gray-400': row.task.completed }" data-testid="task-description">
                {{ row.task.description || '(no description)' }}
              </div>
              <div
                class="mt-0.5 flex items-center gap-1.5 overflow-hidden whitespace-nowrap text-xs"
                [ngClass]="row.overdue ? '' : 'text-[var(--textColorFaded)]'">
                <span class="min-w-0 truncate" data-testid="task-note-title">{{ row.noteTitle }}</span>
                @if (row.task.ownerId) {
                  <span aria-hidden="true">·</span>
                  <span class="inline-flex flex-shrink-0 items-center gap-0.5" data-testid="task-owner"><i class="pi pi-user !text-[0.65rem]" aria-hidden="true"></i>{{ row.ownerName }}</span>
                }
                @if (row.task.dueDate) {
                  <span aria-hidden="true">·</span>
                  <span class="flex-shrink-0"><ng-container *ngTemplateOutlet="dueMark; context: { $implicit: row }"></ng-container></span>
                }
              </div>
            </div>
          </div>
        } @empty {
          <div class="px-3 py-6 text-center text-sm text-[var(--textColorFaded)]" data-testid="task-empty">{{ emptyMessage }}</div>
        }
      }
    </div>
  } @else {
    <!-- Same table as the List view: drag a header edge to resize a column (widths are remembered). -->
    <p-table
      #desktopTable
      [value]="loading ? [] : rows"
      [rowTrackBy]="trackRow"
      [resizableColumns]="true"
      columnResizeMode="expand"
      stateStorage="local"
      [stateKey]="desktopTableStateKey"
      styleClass="notes-list-table task-list-table p-datatable-sm p-datatable-gridlines text-[var(--textColor)] bg-primary border border-black rounded-md"
      tableStyleClass="w-full"
      (onColResize)="onColumnResizeEnd()">
      <ng-template pTemplate="header">
        <tr>
          @if (isSelectMode) {
            <th class="list-select-col !text-center border border-black p-0" [attr.style]="selectColumnStyle" scope="col" aria-label="Select"></th>
          }
          @for (col of columns; track col) {
            <th pResizableColumn scope="col" class="border border-black p-0" [style.width]="defaultColumnWidths[col]" [attr.aria-sort]="ariaSort(col)">
              <button
                type="button"
                class="flex w-full cursor-pointer items-center gap-1 border-0 bg-transparent px-1 py-1 text-left font-semibold text-inherit"
                [ngClass]="{ 'justify-center': col === 'Completed' || col === 'Owner' || col === 'Due' }"
                (click)="sortData(col)"
                [attr.data-testid]="'task-sort-' + col">
                <span class="truncate">{{ columnLabel(col) }}</span>
                @if (sortBy === col) {
                  <span class="material-icons text-xs" aria-hidden="true">{{ sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                }
              </button>
            </th>
          }
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-row>
        <tr
          tabindex="0"
          class="cursor-pointer bg-primary-100 text-black hover:bg-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:!bg-slate-700"
          [ngClass]="{
            'italic !text-gray-500 dark:!text-gray-400': row.task.completed,
            'task-overdue !text-red-700 dark:!text-red-400': row.overdue,
            'ring-2 ring-inset ring-primary-600': isSelectMode && isSelected(row)
          }"
          (click)="onRowClick(row)"
          (keydown.enter)="onRowClick(row)"
          [attr.aria-label]="(row.overdue ? 'Overdue: ' : '') + row.task.description + ', in ' + row.noteTitle + (isSelectMode ? (isSelected(row) ? '. Selected.' : '.') : '. Opens the note in Edit at this task.')"
          data-testid="task-row">
          @if (isSelectMode) {
            <td class="border border-[var(--borderColor)] !text-center" [attr.style]="selectColumnStyle" (click)="$event.stopPropagation()">
              <p-checkbox
                [binary]="true"
                [ngModel]="isSelected(row)"
                (ngModelChange)="toggleSelected(row, $event)"
                [ariaLabel]="'Select task ' + row.task.description"
                styleClass="cursor-pointer"
                data-testid="task-select"></p-checkbox>
            </td>
          }
          <td class="max-w-0 truncate border border-[var(--borderColor)]" [title]="row.task.description" data-testid="task-description">
            <span [class.line-through]="row.task.completed">{{ row.task.description || '(no description)' }}</span>
          </td>
          <td class="max-w-0 truncate border border-[var(--borderColor)]" [title]="row.noteTitle" data-testid="task-note-title">{{ row.noteTitle }}</td>
          <td
            class="task-completed-cell !text-center border border-[var(--borderColor)]"
            [class.task-completed-cell--active]="activeCompletedKey === row.key"
            [title]="completedCellTitle(row)"
            (click)="onCompletedCellClick(row, $event)"
            data-testid="task-completed-cell">
            <button
              type="button"
              class="inline-flex cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-inherit"
              [attr.aria-pressed]="row.task.completed"
              [attr.aria-label]="(row.task.completed ? 'Done' : 'Open') + (activeCompletedKey === row.key ? ' — press again to toggle' : ' — press to select, again to toggle')"
              (keydown.enter)="$event.stopPropagation()">
              <ng-container *ngTemplateOutlet="completedMark; context: { $implicit: row }"></ng-container>
            </button>
          </td>
          <td class="max-w-0 truncate !text-center border border-[var(--borderColor)]" [title]="row.ownerName" data-testid="task-owner">{{ row.ownerName }}</td>
          <td class="!text-center border border-[var(--borderColor)]" data-testid="task-due-cell">
            <ng-container *ngTemplateOutlet="dueMark; context: { $implicit: row }"></ng-container>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          @if (loading) {
            <td [attr.colspan]="isSelectMode ? 6 : 5" class="px-3 py-6 text-center text-sm text-[var(--textColorFaded)]" data-testid="task-loading">Loading tasks…</td>
          } @else {
            <td [attr.colspan]="isSelectMode ? 6 : 5" class="px-3 py-6 text-center text-sm text-[var(--textColorFaded)]" data-testid="task-empty">{{ emptyMessage }}</td>
          }
        </tr>
      </ng-template>
    </p-table>
  }
</div>

<!-- Select mode → Change due date… -->
<p-dialog
  header="Change due date"
  [visible]="dueDateDialogVisible"
  (visibleChange)="dueDateDialogVisible = $event"
  [modal]="true"
  [dismissableMask]="true"
  appendTo="body"
  [style]="{ width: 'min(22rem, calc(100vw - 2rem))' }">
  <p class="mb-2 text-sm">New due date for {{ selectedCount }} selected task{{ selectedCount === 1 ? '' : 's' }}:</p>
  <div class="mb-2 flex flex-wrap gap-2">
    <p-button label="Today" size="small" severity="secondary" [outlined]="true" (onClick)="setDueDateQuick('today')"></p-button>
    <p-button label="Tomorrow" size="small" severity="secondary" [outlined]="true" (onClick)="setDueDateQuick('tomorrow')"></p-button>
    <p-button label="Next week" size="small" severity="secondary" [outlined]="true" (onClick)="setDueDateQuick('next-week')"></p-button>
  </div>
  <p-datepicker
    [(ngModel)]="dueDateValue"
    [inline]="true"
    [firstDayOfWeek]="1"
    [showWeek]="false"
    styleClass="w-full"
    data-testid="task-due-picker"></p-datepicker>
  <ng-template pTemplate="footer">
    <div class="flex w-full flex-wrap justify-between gap-2">
      <p-button label="Clear due date" severity="secondary" [text]="true" (onClick)="applyDueDate(true)" data-testid="task-due-clear"></p-button>
      <div class="flex gap-2">
        <p-button label="Cancel" severity="secondary" [outlined]="true" (onClick)="dueDateDialogVisible = false"></p-button>
        <p-button label="Apply" [disabled]="!dueDateValue" (onClick)="applyDueDate()" data-testid="task-due-apply"></p-button>
      </div>
    </div>
  </ng-template>
</p-dialog>

<!-- Select mode → Delete tasks… -->
<p-dialog
  header="Delete tasks"
  [visible]="deleteDialogVisible"
  (visibleChange)="deleteDialogVisible = $event"
  [modal]="true"
  appendTo="body"
  [style]="{ width: 'min(24rem, calc(100vw - 2rem))' }">
  <p class="text-sm">
    Delete {{ selectedCount }} selected task{{ selectedCount === 1 ? '' : 's' }}? Each task is removed from its note, together
    with its text.
  </p>
  <ng-template pTemplate="footer">
    <div class="flex justify-end gap-2">
      <p-button label="Cancel" severity="secondary" [outlined]="true" (onClick)="deleteDialogVisible = false"></p-button>
      <p-button label="Delete" severity="danger" icon="pi pi-trash" (onClick)="confirmDelete()" data-testid="task-delete-confirm"></p-button>
    </div>
  </ng-template>
</p-dialog>

<!-- Print-only sheet (moved to <body>; see TaskListComponent.printSheet): title, what's listed, and a
     plain table whose cells wrap, so it fits the page width on any paper size. -->
<div #printSheet class="task-print-sheet" aria-hidden="true">
  <h1 class="task-print-sheet__title">Tasks</h1>
  <p class="task-print-sheet__meta">{{ printSummary }} · Printed {{ printedAt }}</p>
  <table class="task-print-sheet__table">
    <colgroup>
      <col style="width: 36%" />
      <col style="width: 25%" />
      <col style="width: 9%" />
      <col style="width: 15%" />
      <col style="width: 15%" />
    </colgroup>
    <thead>
      <tr>
        <th>Task</th>
        <th>Note title</th>
        <th class="task-print-sheet__center">Completed</th>
        <th class="task-print-sheet__center">Owner</th>
        <th class="task-print-sheet__center">Due date</th>
      </tr>
    </thead>
    <tbody>
      @for (row of rows; track row.key) {
        <tr [class.task-print-sheet__overdue]="row.overdue" [class.task-print-sheet__done]="row.task.completed">
          <td><span class="task-print-sheet__desc">{{ row.task.description || '(no description)' }}</span></td>
          <td>{{ row.noteTitle }}</td>
          <td class="task-print-sheet__center">{{ row.task.completed ? '☑ Done' : '☐ Open' }}</td>
          <td class="task-print-sheet__center">{{ row.ownerName }}</td>
          <td class="task-print-sheet__center">{{ printDue(row) }}{{ row.overdue ? ' (overdue)' : '' }}</td>
        </tr>
      } @empty {
        <tr><td colspan="5">{{ emptyMessage }}</td></tr>
      }
    </tbody>
  </table>
</div>
